'use client';

/**
 * useFinopsStrategyData — React Query wrapper for the FinOps MC cards.
 *
 * The three FinOps cards (forecast burn / time-to-target / monthly
 * breakdown) predate the card-export protocol and fetched via a raw
 * `api.getStrategyData` + `useEffect` + `useState`. That kept their
 * responses out of the React Query cache — so the scene-capture
 * envelope, whose payload half is derived as "union of mounted cards'
 * query_keys looked up in the cache", could never record what these
 * cards were showing.
 *
 * This hook is `useStrategyData` (lib/api/hooks.ts) with one delta:
 * `placeholderData: keepPreviousData`. Stepping a simulation chip
 * (cash target, projected users) changes the query key; without
 * keepPreviousData the chart would blank to a spinner on every step,
 * where the old effect+state pattern kept the previous chart visible
 * while the new simulation ran. Same canonical
 * `['strategy-data', strategy, params]` key shape, so captures and
 * invalidation treat these like every other strategy query.
 *
 * @layer presentation
 * @arch_component_type hook
 * @arch_domain canvas
 */

import { keepPreviousData, useQuery, type QueryKey } from '@tanstack/react-query';
import { api } from '@/lib/api/client';

export function useFinopsStrategyData<T>(
  strategy: string | null,
  params: Record<string, unknown>,
) {
  // Surfaced so cards pass it straight to useCardExport({query_key}) —
  // the hook that holds the key is the hook that returns the data; a
  // card reconstructing the key is a second copy that drifts (capture
  // rule 1 in klyde-cards).
  const queryKey: QueryKey = ['strategy-data', strategy, params];
  const query = useQuery({
    queryKey,
    queryFn: () => api.getStrategyData<T>(strategy!, params),
    enabled: !!strategy,
    staleTime: 15 * 60 * 1000,
    retry: false, // don't retry failed simulation calls
    placeholderData: keepPreviousData,
  });
  return { ...query, queryKey };
}
